// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'tiny... Remove this comment to see the full error message
import tinymce from 'tinymce';
import I18n from 'I18n';
import UILink from 'UIComponents/link/UILink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { compose, curry } from 'rich-text-lib/utils/iterables';
import { i18nNameSpace, hsformattingConfig, UnsupportedFonts } from '../constants/hsformatting';
import { CHANGE_FONT } from '../constants/usageTracking';
import { addPreProcessTransform, addPostProcessTransform } from '../utils/transformer';
import { HS_FORMATTING } from '../constants/plugins';
import { trackInteraction } from '../utils/usageTracking';
import { maybeGetComputedStyle } from './getComputedStyle';
import { getPluginSetting } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const CLEAN_FONT_STACK_QUOTES_REGEXP = /['"]/g;
const CLEAN_FONT_STACK_COMMAS_REGEXP = /(, )/g;
const split = curry((separator, str) => str.split(separator));
const normalizeFontStack = fontStack => {
  return fontStack.replace(CLEAN_FONT_STACK_QUOTES_REGEXP, '').replace(CLEAN_FONT_STACK_COMMAS_REGEXP, ',').toLowerCase().trim();
};
const normalizeAndFlattenFontValue = compose(split(','), normalizeFontStack);
export const matchFont = (computedFontFamily, editorFonts) => {
  const computedFontFamilyPrimary = normalizeAndFlattenFontValue(computedFontFamily)[0];
  return editorFonts.find(fontFamily => {
    const fontFamilyPrimary = normalizeFontStack(fontFamily.value).split(',')[0];
    return computedFontFamilyPrimary === fontFamilyPrimary;
  });
};
export const getCustomFontSelectOptions = customFonts => {
  return customFonts.map(font => {
    const name = font.family || font.name;
    return Object.assign({}, font, {
      value: name + (font.fallback ? `, ${font.fallback}` : ''),
      family: name
    });
  });
};
export const getFontSelectOptions = fonts => {
  if (!fonts) {
    return [];
  }
  const unsupportedFontLink = /*#__PURE__*/_jsx(UILink, {
    external: true,
    href: "https://blog.hubspot.com/website/web-safe-html-css-fonts",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${i18nNameSpace}.fontFamily.unsupportedFontLink`
    })
  });
  const help = fontName => /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: `${i18nNameSpace}.fontFamily.unsupportedFontErrorText`,
    options: {
      fontName,
      unsupportedFontLink
    }
  });
  return Object.keys(fonts).sort().map(font => {
    const disabled = UnsupportedFonts.includes(fonts[font]);
    return Object.assign({
      text: font,
      value: fonts[font],
      disabled
    }, disabled ? {
      help: help(font)
    } : {});
  });
};
export const parseFonts = (editor, fonts, useParsedFonts) => {
  const unknownFonts = [];
  const usedUnsupportedFonts = [];

  // Should we parse the entire document
  const element = editor.settings.inline ? editor.getElement() : editor.getBody();
  const flatFonts = fonts.reduce((acc, value) => {
    return [...acc, ...normalizeAndFlattenFontValue(value)];
  }, []);
  const walker = new tinymce.dom.TreeWalker(element);
  do {
    const current = walker.current();
    if (current.nodeType !== 1) {
      continue;
    }
    const fontFamily = getComputedStyle(current).fontFamily;
    const normalFlatFontFamilyPrimary = normalizeAndFlattenFontValue(fontFamily)[0];
    if (UnsupportedFonts.includes(normalFlatFontFamilyPrimary)) {
      usedUnsupportedFonts.push(normalFlatFontFamilyPrimary);
      continue;
    }
    if (unknownFonts.includes(fontFamily) || flatFonts.includes(normalFlatFontFamilyPrimary)) {
      continue;
    }
    if (useParsedFonts) {
      unknownFonts.push(fontFamily);
    }
  } while (element.contains(walker.next()));
  return {
    unknownFonts,
    usedUnsupportedFonts
  };
};
export const calculateParsedFonts = (editor, useParsedFonts, fonts, webfonts) => {
  const {
    unknownFonts = [],
    usedUnsupportedFonts
  } = parseFonts(editor, [...fonts.map(({
    value: fontValue
  }) => fontValue), ...webfonts.map(({
    value: webFontValue
  }) => webFontValue)], useParsedFonts);
  const fontsWithUsedUnsupportedFonts = fonts.filter(({
    value
  }) => {
    if (UnsupportedFonts.includes(value)) {
      return usedUnsupportedFonts.includes(value);
    }
    return true;
  });
  return {
    parsedFonts: unknownFonts.map(unknownFontValue => ({
      value: unknownFontValue,
      text: unknownFontValue.split(',')[0].replace(/['"]/g, '')
    })),
    fontsWithUsedUnsupportedFonts
  };
};
export const constructParsedFontText = isEmail => ({
  help: I18n.text(`${i18nNameSpace}.fontFamily.${isEmail ? 'email' : 'pages'}.templateFontTooltip`),
  text: I18n.text(`${i18nNameSpace}.fontFamily.templateFontGroupName`)
});
export const getHandleChangeFont = editor => ({
  target: {
    value
  }
}, variantOption) => {
  const isCustomFont = !!variantOption;
  const valueToUse = isCustomFont ? variantOption : value;
  editor.execCommand('hsfontselect', false, {
    value: valueToUse,
    isCustomFont
  });
  trackInteraction(editor, 'tinymceFormatting', CHANGE_FONT, {
    method: 'button',
    value,
    isCustomFont
  });
};
const createNodeChangeHandler = (handlerFunc, inherits = false) => {
  return e => {
    const {
      element,
      target,
      parents
    } = e;
    const win = target.getWin();
    let computedStyle = maybeGetComputedStyle(win, element) || {};
    const elementHasStyle = handlerFunc(computedStyle);
    if (!elementHasStyle && inherits) {
      //some styles will inherit an active style from the parent no matter what (underline)
      //check all the parents to see if they have the style even though the child doesn't
      return !!parents.find(parent => {
        computedStyle = maybeGetComputedStyle(win, parent) || {};
        return handlerFunc(computedStyle);
      });
    }
    return elementHasStyle;
  };
};
const getLineHeightDisplayValue = (win, element) => {
  const computedStyle = maybeGetComputedStyle(win, element);
  if (!computedStyle) {
    return null;
  }
  const computedLineHeight = computedStyle.getPropertyValue('line-height');
  const computedFontSize = computedStyle.getPropertyValue('font-size');
  const parsedLineHeight = parseFloat(computedLineHeight);
  const parsedFontSize = parseFloat(computedFontSize);
  if (!isNaN(parsedLineHeight) && !isNaN(parsedFontSize)) {
    return parsedLineHeight / parsedFontSize;
  }
  return null;
};
export const nodeChangeHandlers = {
  bold: createNodeChangeHandler(computedStyle => {
    if (computedStyle.fontWeight === 'bold' || Number(computedStyle.fontWeight) >= 600) {
      return true;
    }
    return false;
  }),
  underline: createNodeChangeHandler(computedStyle => {
    return computedStyle.textDecoration.includes('underline');
  }, true),
  italic: createNodeChangeHandler(computedStyle => {
    return computedStyle.fontStyle.includes('italic');
  })
};
export const overrideInheritedStyles = {
  bold: {
    on: 'bold',
    off: 'normal',
    affectedStyles: ['font-weight'],
    inlineElements: ['b', 'strong'],
    tinyMCECommand: 'bold'
  },
  italic: {
    on: 'italic',
    off: 'normal',
    affectedStyles: ['font-style'],
    inlineElements: ['i', 'em'],
    tinyMCECommand: 'italic'
  },
  underline: {
    on: 'underline',
    off: 'none',
    affectedStyles: ['text-decoration'],
    inlineElements: ['u'],
    tinyMCECommand: 'underline'
  }
};
export const getMatchingValueHandler = (editor, format, values) => nodeChangeEvent => {
  const {
    parents,
    element
  } = nodeChangeEvent;
  const {
    formatter
  } = editor;
  let newControlValue;
  for (let i = 0; i < parents.length; i++) {
    const matchingItem = values.find(item => formatter.matchNode(parents[i], format, {
      value: item.value
    }));
    if (matchingItem) {
      newControlValue = matchingItem.value;
      break;
    }
  }
  if (!newControlValue) {
    const maybeLineHeightDisplayValue = getLineHeightDisplayValue(editor.getWin(), element);
    const matched = values.find(item => item.value === maybeLineHeightDisplayValue);
    newControlValue = matched ? matched.value : null;
  }
  return newControlValue;
};
export const getListNode = (editor, selectedNode) => {
  if (!selectedNode) return null;
  return editor.dom.getParent(selectedNode, 'li', editor.getBody());
};
export const isListParentNode = selectedNode => {
  if (!selectedNode) return null;
  return ['OL', 'UL'].includes(selectedNode.nodeName);
};
export const customFontFunctionHublRegex = new RegExp(/{{\s+include_custom_fonts((.*?))\s+}}/g);
const createCustomFontsHubl = (customFonts, usedCustomFonts) => {
  const hublFonts = {};
  if (!usedCustomFonts.length) {
    return '';
  }
  usedCustomFonts.forEach(customFontName => {
    const currentCustomFont = customFonts.find(customFont => customFontName === customFont.family);
    if (!currentCustomFont) return;
    const {
      variants
    } = currentCustomFont;
    if (!variants || !variants.length) return;

    // there are currently 2 types of custom fonts - dev added theme and marketer added branding
    // developer added theme custom fonts have variants as type string[]
    // marketer uploaded brand custom fonts have variants as type { name: string, ... }
    if (typeof variants[0] === 'string') {
      hublFonts[customFontName] = currentCustomFont.variants;
    } else if (variants[0].name) {
      hublFonts[customFontName] = variants.map(variant => variant.name);
    }
  });
  return `{{ include_custom_fonts(${JSON.stringify(hublFonts)}) }}`;
};
const parseUsedCustomFonts = (editor, customFonts) => {
  const usedSupportedCustomFonts = {};
  const element = editor.settings.inline ? editor.getElement() : editor.getBody();
  const walker = new tinymce.dom.TreeWalker(element);
  do {
    const current = walker.current();
    if (current.nodeType !== 1) {
      continue;
    }
    const fontFamily = getComputedStyle(current).fontFamily;
    const matchedFont = matchFont(fontFamily, customFonts);
    if (matchedFont) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      usedSupportedCustomFonts[matchedFont.family] = true;
    }
  } while (element.contains(walker.next()));
  return Object.keys(usedSupportedCustomFonts);
};
export const appendCustomFontsHublToContent = (editor, content, customFonts) => {
  const fontsUsed = parseUsedCustomFonts(editor, customFonts);
  const customFontsHubl = createCustomFontsHubl(customFonts, fontsUsed);
  return content + customFontsHubl;
};
export const extractCustomFontsHublFromContent = domContent => {
  let content = domContent;
  let hublCustomFontsTagsLeftInDomContent = customFontFunctionHublRegex.exec(content);
  while (hublCustomFontsTagsLeftInDomContent !== null) {
    if (hublCustomFontsTagsLeftInDomContent.length) {
      content = content.replace(hublCustomFontsTagsLeftInDomContent[0], '');
    }
    hublCustomFontsTagsLeftInDomContent = customFontFunctionHublRegex.exec(content);
  }
  return content;
};
export const addCustomFontPreProcessTransform = editor => {
  addPreProcessTransform(editor, dom => {
    dom.body.innerHTML = extractCustomFontsHublFromContent(dom.body.innerHTML);
  });
};
export const addCustomFontPostProcessTransform = (editor, customFonts) => {
  addPostProcessTransform(editor, dom => {
    dom.body.innerHTML = appendCustomFontsHublToContent(editor, dom.body.innerHTML, customFonts);
  });
};
export const getCustomFonts = editor => {
  const {
    [hsformattingConfig.FONT_FORMATS]: fontFormats
  } = getPluginSetting(editor, HS_FORMATTING);
  return getCustomFontSelectOptions(fontFormats.customFonts || []);
};